/**
 * Admin · Unified Documents hub.
 *
 * Aggregates every document across modules — admissions, incidents, billing
 * invoices, MAR records, and the legacy /documents collection — into a
 * single browsable, filterable index.
 */
import { useEffect, useState } from "react";
import { FileText, FileDown, Search } from "lucide-react";
import { api, API } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader, AdminEmpty } from "./_shared";

const MODULE_KEYS = ["all", "uploads", "admissions", "incidents", "billing", "mar", "vendor"];

const MODULE_TONE = {
  uploads: "bg-[#EEF0F4] text-[#525252]",
  admissions: "bg-[#E6EFE8] text-[#1F3A2A]",
  incidents: "bg-[#FCE2E2] text-[#A04545]",
  billing: "bg-[#F4E5C5] text-[#7A5E26]",
  mar: "bg-[#E6DCFB] text-[#5B3DB8]",
  vendor: "bg-[#EFE6FB] text-[#5B3DB8]",
};

export default function AdminDocumentsHub() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [module, setModule] = useState("all");
  const [q, setQ] = useState("");

  const moduleLabel = (key) => t(`admin_panel.documents_hub.module_${key}`) || key;

  const reload = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/documents/unified", { params: { module, q: q || null, limit: 200 } }),
        api.get("/documents/unified/counts"),
      ]);
      setItems(r.data || []);
      setCounts(c.data || {});
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    reload();
  };

  const fullUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return API + path.replace(/^\/api/, "");
  };

  return (
    <div data-testid="admin-documents-hub" className="space-y-7">
      <AdminHeader
        eyebrow={t("admin_panel.documents_hub.eyebrow")}
        title={t("admin_panel.documents_hub.title")}
      />

      <form onSubmit={onSearchSubmit} className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} strokeWidth={1.6} className="absolute start-3 top-1/2 -translate-y-1/2 text-[#525252]" />
          <input
            type="text"
            placeholder={t("admin_panel.documents_hub.search_placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="docs-hub-search"
            className="orbit-input ps-9"
          />
        </div>
        <button type="submit" className="btn-ghost !py-2 !px-4 !text-xs">
          {t("admin_panel.documents_hub.search_button")}
        </button>
      </form>

      <div className="flex flex-wrap gap-2">
        {MODULE_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setModule(key)}
            data-testid={`docs-hub-chip-${key}`}
            className={`rounded-full px-4 py-2 text-xs font-medium tracking-[0.16em] uppercase transition-colors ${
              module === key
                ? "bg-[#132619] text-white"
                : "bg-white border hairline text-[#525252] hover:bg-[#F2EFEA]"
            }`}
          >
            {moduleLabel(key)}
            {counts[key] != null && (
              <span className="ms-2 text-[10px] opacity-70">{counts[key]}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="font-serif italic text-[#525252]">{t("admin_panel.documents_hub.loading")}</p>
      ) : items.length === 0 ? (
        <AdminEmpty testid="admin-documents-hub-empty">
          {t("admin_panel.documents_hub.empty_state")}
        </AdminEmpty>
      ) : (
        <ul
          data-testid="admin-documents-hub-list"
          className="rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6"
        >
          {items.map((d) => (
            <li
              key={d.id}
              data-testid={`docs-hub-row-${d.id}`}
              className="px-5 py-4 flex items-start gap-4"
            >
              <span className="h-10 w-10 rounded-2xl bg-[#FAF6EE] flex items-center justify-center shrink-0">
                <FileText size={16} strokeWidth={1.6} className="text-[#525252]" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline flex-wrap gap-2">
                  <span className="font-serif text-base text-[#171717]">{d.title}</span>
                  <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full ${MODULE_TONE[d.source_module] || ""}`}>
                    {moduleLabel(d.source_module)}
                  </span>
                  {d.badge && (
                    <span className="text-[10px] tracking-[0.18em] uppercase text-[#525252]">
                      {d.badge}
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#525252] mt-1 flex flex-wrap gap-3">
                  {d.child_name && <span>{d.child_name}</span>}
                  {d.kind && <span>{d.kind}</span>}
                  {d.created_at && (
                    <span>{new Date(d.created_at).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
              {d.downloadable_url && (
                <a
                  href={fullUrl(d.downloadable_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`docs-hub-open-${d.id}`}
                  className="inline-flex items-center gap-1.5 text-xs text-[#1F3A2A] border hairline rounded-full px-3 py-1.5 hover:bg-[#FAF6EE]"
                >
                  <FileDown size={12} strokeWidth={1.6} />
                  {t("admin_panel.documents_hub.open")}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
